import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ContractsAPI from "../api/ContractsAPI";
import { useStoreContractLocale } from "./useStoreContractLocale";
import { toastService } from "../services/ToastService";
import debounce from "../shared/debounce";

type GeneralData = {
    companyName: string;
    companyAddress: string;
    taxID: string;
    logoUrl: string;
  };
  
  type Conditions = {
    conditions: string[];
  };
  
export type Instalment = {
  percentage: number;
  method: 'Card' | 'Transfer' | 'Cash';
};

type PaymentMethod = {
  instalments: Instalment[];
  paymentSpecifications: string;
};
  
  type Remarks = {
    conditions: string[];
  };
  
  type AdditionalRequirements = {
    requiresFollowUpMeeting: boolean;
    requiresEmployeeSignature: boolean;
  };
  
export type ContractTemplate = {
    general: GeneralData;
    conditions: Conditions;
    payment: PaymentMethod;
    remarks: Remarks;
    additionalRequirements: AdditionalRequirements;
    locale: string;
  };  

type UseContractTemplateReturnType = {
  contractTemplate: ContractTemplate;
  updateContractTemplate: (updatedContractTemplate: Partial<ContractTemplate>) => void;
  locale: string;
  setLocale: any;
};

export function useContractTemplate(): UseContractTemplateReturnType {
  const storeContractLocale = useStoreContractLocale();
  const [locale, setLocale] = useState<string>(storeContractLocale);
  const { t } = useTranslation();

  const [contractTemplate, setContractTemplate] = useState<ContractTemplate>({
    general: {
      companyName: "Sample Co.",
      companyAddress: "123 Sample St., Example City",
      taxID: "123-456-789",
      logoUrl: ""
    },
    conditions: {
      conditions: ["Condition 1", "Condition 2"],
    },
    payment: {
      instalments: [],
      paymentSpecifications: ''
    },
    remarks: {
      conditions: ["Remark 1", "Remark 2"],
    },
    additionalRequirements: {
      requiresFollowUpMeeting: false,
      requiresEmployeeSignature: false,
    },
    locale: "EN_US"
  });

  useEffect(() => {
    ContractsAPI.getTemplate(locale).then(response => {
      setContractTemplate(response.data);
    }).catch(error => {
      console.error('Error fetching contract template:', error);
    });
  }, [locale]);

  const templateRef = useRef(contractTemplate);
  templateRef.current = contractTemplate;

  const flushSave = useCallback(() => {
    const toSave = templateRef.current;
    ContractsAPI.save(locale, toSave)
      .then(() => {
        toastService.showToast(
          t("global.changes_saved") || "Changes saved",
          "success"
        );
      })
      .catch(error => {
        console.error('Error saving contract template:', error);
      });
  }, [locale, t]);

  const debouncedSave = useMemo(() => debounce(flushSave, 500), [flushSave]);

  const updateContractTemplate = (updatedContractTemplate: Partial<ContractTemplate>) => {
    setContractTemplate(prev => {
      const completeTemplate: ContractTemplate = {
        ...prev,
        ...updatedContractTemplate,
      };
      templateRef.current = completeTemplate;
      return completeTemplate;
    });
    debouncedSave();
  };

  return {
    contractTemplate,
    updateContractTemplate,
    locale, 
    setLocale
  };
}
